// Tableau de suivi de l'organisation des chantiers.
//
// Une ligne par chantier, une colonne par étape à préparer : acompte,
// occupation du sol, commande de fournitures, client averti… Chaque case vaut
// « fait », « à faire » ou « non concerné », et le tableau se lit d'un coup
// d'œil : ce qui est vert est réglé, ce qui est rouge attend quelqu'un.
//
// Les colonnes ne sont plus écrites en dur : elles vivent en base (migration
// 19) et l'utilisateur en ajoute. Ce module ne connaît donc AUCUNE étape par
// son nom — il travaille sur la liste qu'on lui passe.
//
// Une colonne a une SOURCE. « manuel » : quelqu'un coche, comme avant. Sinon,
// le CRM répond tout seul — un acompte facturé coche la case Acompte, un devis
// signé coche la case Devis. Ce que le CRM sait, il ne faut pas le redemander :
// une case cochée à la main finit toujours par mentir.
//
// Fonctions PURES : aucun accès réseau ni base, donc testables ligne à ligne.

/** L'état d'une case du tableau. */
export type StatutPrep = 'na' | 'todo' | 'done';

export const STATUTS_PREP: readonly StatutPrep[] = ['todo', 'done', 'na'];

export const LIBELLES_STATUT_PREP: Record<StatutPrep, string> = {
  na: 'Non concerné',
  todo: 'À faire',
  done: 'Fait',
};

/**
 * D'où vient l'état d'une colonne : de la main de quelqu'un, ou d'un fait que
 * le CRM connaît. Une source inconnue (colonne créée par une version plus
 * récente) se comporte comme « manuel » plutôt que de casser l'écran.
 */
export type SourceColonne = 'manuel' | 'devis_signe' | 'acompte' | 'facture_emise';

export const SOURCES_AUTOMATIQUES: readonly SourceColonne[] = [
  'devis_signe',
  'acompte',
  'facture_emise',
];

/** Cette colonne se remplit-elle toute seule ? */
export function colonneAutomatique(c: ColonneSuivi): boolean {
  return SOURCES_AUTOMATIQUES.includes(c.source);
}

/** Une colonne du tableau, telle qu'elle est enregistrée. */
export interface ColonneSuivi {
  id: string;
  cle: string;
  label: string;
  /** Phrase de l'alerte quand l'étape reste « à faire » ; null = pas d'alerte. */
  alerte: string | null;
  ordre: number;
  actif: boolean;
  /** « manuel » par défaut : c'est le comportement d'origine. */
  source: SourceColonne;
}

/**
 * Ce que le CRM sait d'un chantier, sans que personne ait rien coché.
 *
 * `suiviParLeCrm` distingue « c'est faux » de « on n'en sait rien » : un
 * chantier d'avant le CRM, dont les devis et factures vivent ailleurs, ne doit
 * pas être déclaré en retard sur des étapes qu'il a peut-être franchies.
 */
export interface FaitsChantier {
  devisSigne: boolean;
  acompteEmis: boolean;
  factureEmise: boolean;
  suiviParLeCrm: boolean;
}

export const AUCUN_FAIT: FaitsChantier = {
  devisSigne: false,
  acompteEmis: false,
  factureEmise: false,
  suiviParLeCrm: false,
};

/** Un chantier, réduit à ce qui sert au tableau. */
export interface ChantierSuivi {
  id: string;
  numero: string;
  libelle: string;
  statut: string;
  contactId: string | null;
  ville: string | null;
  /** Date prévue de début, 'AAAA-MM-JJ' ou null si pas encore posée. */
  datePrevueDebut: string | null;
  /** Cases cochées, par clé de colonne. Une clé absente vaut « non concerné ». */
  prep: Partial<Record<string, StatutPrep>>;
  /** Ce que le CRM sait, pour les colonnes qui ne se cochent pas à la main. */
  faits: FaitsChantier;
}

export interface AvancementSuivi {
  fait: number;
  aFaire: number;
  nonConcerne: number;
  /** Nombre de colonnes qui comptent (tout sauf « non concerné »). */
  concerne: number;
  /** Part du travail de préparation déjà faite, 0 à 100 ; 100 si rien ne concerne le chantier. */
  pourcentage: number;
}

export interface LigneSuivi {
  chantier: ChantierSuivi;
  /** Le statut de CHAQUE colonne active, dans l'ordre des colonnes. */
  cases: { colonne: ColonneSuivi; statut: StatutPrep }[];
  avancement: AvancementSuivi;
  /** Jours avant le début : négatif si le chantier a déjà commencé, null sans date. */
  joursAvant: number | null;
  /** Le chantier démarre (ou a démarré) alors qu'il reste des étapes à faire. */
  enRetard: boolean;
}

/** Chantiers hors jeu : ils n'attendent plus aucune préparation. */
const STATUTS_CLOS = new Set(['termine', 'annule', 'archive']);

/** Un chantier « prospect » est encore un devis : rien à préparer tant qu'il n'est pas signé. */
const STATUTS_HORS_SUIVI = new Set([...STATUTS_CLOS, 'prospect']);

const JOUR_MS = 86_400_000;

export function chantierASuivre(statut: string): boolean {
  return !STATUTS_HORS_SUIVI.has(statut);
}

/**
 * Jours entiers entre aujourd'hui et une date 'AAAA-MM-JJ'.
 * Positif = à venir, négatif = passé, 0 = aujourd'hui.
 */
export function joursAvant(jour: string | null, maintenantMs: number): number | null {
  if (!jour) return null;
  const cible = Date.parse(`${jour}T00:00:00Z`);
  if (Number.isNaN(cible)) return null;
  const aujourdhui = new Date(maintenantMs);
  const debutJour = Date.UTC(
    aujourdhui.getUTCFullYear(),
    aujourdhui.getUTCMonth(),
    aujourdhui.getUTCDate(),
  );
  return Math.round((cible - debutJour) / JOUR_MS);
}

/**
 * Clé technique tirée d'un libellé saisi à l'écran : « Échafaudage commandé »
 * devient `echafaudage_commande`. La clé relie la colonne aux cases déjà
 * cochées, donc elle ne change JAMAIS après création — d'où l'unicité forcée
 * ici, par un suffixe numéroté plutôt qu'un refus.
 */
export function cleDepuisLabel(label: string, dejaPrises: readonly string[] = []): string {
  const base =
    label
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 28) || 'colonne';
  const prises = new Set(dejaPrises);
  if (!prises.has(base)) return base;
  for (let n = 2; n < 1000; n += 1) {
    const essai = `${base.slice(0, 28 - String(n).length - 1)}_${String(n)}`;
    if (!prises.has(essai)) return essai;
  }
  return `${base.slice(0, 20)}_${String(Date.now()).slice(-6)}`;
}

/** Le statut coché à la main, « non concerné » par défaut. */
export function statutManuel(chantier: ChantierSuivi, cle: string): StatutPrep {
  const v = chantier.prep[cle];
  return v === 'done' || v === 'todo' ? v : 'na';
}

/**
 * Le statut d'une case.
 *
 * Une colonne automatique ne regarde JAMAIS ce qui a été coché : elle répond
 * d'après le CRM. Et tant que le CRM ne suit pas ce chantier — aucun devis,
 * aucune facture chez lui — elle répond « non concerné » plutôt que « à
 * faire » : on ne réclame pas un acompte sur un chantier dont on ne sait rien.
 */
export function statutCase(chantier: ChantierSuivi, colonne: ColonneSuivi): StatutPrep {
  if (!colonneAutomatique(colonne)) return statutManuel(chantier, colonne.cle);
  if (!chantier.faits.suiviParLeCrm) return 'na';
  const fait =
    colonne.source === 'devis_signe'
      ? chantier.faits.devisSigne
      : colonne.source === 'acompte'
        ? chantier.faits.acompteEmis
        : chantier.faits.factureEmise;
  return fait ? 'done' : 'todo';
}

/**
 * Le chantier est-il engagé ? C'est-à-dire : le client a-t-il dit oui ?
 *
 * Un devis signé, une facture déjà émise ou un chantier commencé valent
 * engagement. Le reste attend encore une réponse, et n'a rien à faire dans un
 * tableau de préparation — c'est du commerce, pas de l'organisation.
 */
export function chantierEngage(chantier: ChantierSuivi): boolean {
  return (
    chantier.faits.devisSigne ||
    chantier.faits.factureEmise ||
    chantier.faits.acompteEmis ||
    chantier.statut === 'en_cours'
  );
}

/** Statut suivant quand on clique sur une case : à faire → fait → non concerné → … */
export function statutSuivant(statut: StatutPrep): StatutPrep {
  if (statut === 'todo') return 'done';
  if (statut === 'done') return 'na';
  return 'todo';
}

export function avancement(
  chantier: ChantierSuivi,
  colonnes: readonly ColonneSuivi[],
): AvancementSuivi {
  let fait = 0;
  let aFaire = 0;
  let nonConcerne = 0;
  for (const c of colonnes) {
    const s = statutCase(chantier, c);
    if (s === 'done') fait += 1;
    else if (s === 'todo') aFaire += 1;
    else nonConcerne += 1;
  }
  const concerne = fait + aFaire;
  return {
    fait,
    aFaire,
    nonConcerne,
    concerne,
    pourcentage: concerne === 0 ? 100 : Math.round((fait / concerne) * 100),
  };
}

/** Colonnes actives, dans l'ordre d'affichage (ordre, puis libellé). */
export function colonnesActives(colonnes: readonly ColonneSuivi[]): ColonneSuivi[] {
  return colonnes
    .filter((c) => c.actif)
    .slice()
    .sort((a, b) => a.ordre - b.ordre || a.label.localeCompare(b.label, 'fr'));
}

/**
 * Dresse le tableau : une ligne par chantier à préparer, colonnes en ordre.
 *
 * L'ordre des lignes suit l'urgence réelle du terrain — le chantier qui
 * démarre le plus tôt en haut, ceux sans date à la fin, parce qu'une date
 * inconnue ne réclame rien à personne.
 *
 * `maintenantMs` est passé plutôt que lu de l'horloge : la fonction reste pure
 * et les tests peuvent se placer à n'importe quelle date.
 */
export function construireSuivi(
  chantiers: readonly ChantierSuivi[],
  colonnes: readonly ColonneSuivi[],
  maintenantMs: number,
  engagesSeulement = false,
): LigneSuivi[] {
  const actives = colonnesActives(colonnes);
  const lignes: LigneSuivi[] = [];
  for (const ch of chantiers) {
    if (!chantierASuivre(ch.statut)) continue;
    if (engagesSeulement && !chantierEngage(ch)) continue;
    const cases = actives.map((colonne) => ({ colonne, statut: statutCase(ch, colonne) }));
    const av = avancement(ch, actives);
    const j = joursAvant(ch.datePrevueDebut, maintenantMs);
    lignes.push({
      chantier: ch,
      cases,
      avancement: av,
      joursAvant: j,
      enRetard: j !== null && j <= 0 && av.aFaire > 0,
    });
  }
  lignes.sort((a, b) => {
    const ja = a.joursAvant;
    const jb = b.joursAvant;
    if (ja === null && jb === null) return a.chantier.numero.localeCompare(b.chantier.numero, 'fr');
    if (ja === null) return 1;
    if (jb === null) return -1;
    if (ja !== jb) return ja - jb;
    return a.chantier.numero.localeCompare(b.chantier.numero, 'fr');
  });
  return lignes;
}

export interface TotauxSuivi {
  chantiers: number;
  /**
   * Chantiers dont toute la préparation est réglée — et qui ont au moins une
   * étape qui les concerne. Un chantier sur lequel rien n'a été renseigné
   * n'est pas « prêt » : il est vierge, et le compter comme prêt donnait au
   * tableau un optimisme qu'il n'avait pas mérité.
   */
  prets: number;
  /** Chantiers sur lesquels aucune étape n'est encore engagée. */
  vierges: number;
  /** Chantiers qui démarrent alors qu'il reste des étapes à faire. */
  enRetard: number;
  /** Nombre total de cases « à faire », toutes lignes confondues. */
  casesAFaire: number;
}

export function totauxSuivi(lignes: readonly LigneSuivi[]): TotauxSuivi {
  let prets = 0;
  let vierges = 0;
  let enRetard = 0;
  let casesAFaire = 0;
  for (const l of lignes) {
    if (l.avancement.concerne === 0) vierges += 1;
    else if (l.avancement.aFaire === 0) prets += 1;
    if (l.enRetard) enRetard += 1;
    casesAFaire += l.avancement.aFaire;
  }
  return { chantiers: lignes.length, prets, vierges, enRetard, casesAFaire };
}
